/**
 * VM Drive Configuration
 *
 * Supports adding drives from existing disk images in storage pools,
 * or creating new images on the fly.
 */

import { api } from '../api/client.js';
import { apiCall } from '../api/helpers.js';
import type { DiskInfo, PhysicalDrive } from '../api/types.js';
import { escapeHtml, escapeAttr } from '../utils.js';
import { declareLoading, declareLoaded, notificationDialog } from '../ui/dialogs.js';

export interface DriveConfig {
  path: string;
  interface: string;
  cache: string;
  format: string;
}

type OptionMap = Record<string, string>;

interface DriveOptions {
  interfaces: OptionMap;
  cacheModes: OptionMap;
  aioModes: OptionMap;
  formats: OptionMap;
  storagePools: Record<string, StoragePoolInfo>;
  existingDisks: DiskInfo[];
  physicalDrives: PhysicalDrive[];
}

/** Normalize an API options value (array or object) into a value→label map. */
function normalizeOptions(raw: unknown): OptionMap {
  if (Array.isArray(raw)) {
    const map: OptionMap = {};
    for (const v of raw) map[String(v)] = String(v);
    return map;
  }
  return (raw ?? {}) as OptionMap;
}

export interface StoragePoolInfo {
  label: string;
  avail_human: string;
  size_human: string;
  used_pct: number;
}

// Module state
let currentDrives: DriveConfig[] = [];
let driveOptions: DriveOptions | null = null;
let drivesModified = false;
let currentVmName: string | null = null;
let currentHost: string | undefined = undefined;

export function getCurrentDrives(): DriveConfig[] {
  return currentDrives;
}

export function wereDrivesModified(): boolean {
  return drivesModified;
}

export function resetDriveState(): void {
  currentDrives = [];
  driveOptions = null;
  drivesModified = false;
  currentVmName = null;
  currentHost = undefined;
}

/**
 * Open drive configuration dialog
 * @param vmId - VM ID to load drives from, or null for a new VM
 * @param host - Target host to fetch storage resources from (for delegated VMs)
 */
export async function openDrivesDialog(vmId: number | null = null, host?: string): Promise<void> {
  // Store host for use when creating new disk images
  currentHost = host;

  const [options, interfaces, cacheModes, aioModes, formats, storagePools, existingDisks, physicalDrives] = await Promise.all([
    apiCall(api.getOptions(), 'Failed to Load Options'),
    apiCall(api.getDriveInterfaces(), 'Failed to Load Drive Interfaces'),
    apiCall(api.getDriveCacheModes(), 'Failed to Load Cache Modes'),
    apiCall(api.getDriveAioModes(), 'Failed to Load AIO Modes'),
    apiCall(api.getDriveFormats(), 'Failed to Load Drive Formats'),
    apiCall(api.getStoragePools(host), 'Failed to Load Storage Pools'),
    apiCall(api.listDisks(host), 'Failed to Load Disk Images'),
    apiCall(api.listPhysicalDrives(host), 'Failed to Load Physical Drives'),
  ]);
  if (!options) return;

  driveOptions = {
    interfaces: normalizeOptions(interfaces ?? options.drive_interfaces),
    cacheModes: normalizeOptions(cacheModes ?? options.drive_cache_modes),
    aioModes: normalizeOptions(aioModes ?? options.drive_aio_modes),
    formats: normalizeOptions(formats ?? options.drive_formats),
    storagePools: (storagePools ?? {}) as unknown as Record<string, StoragePoolInfo>,
    existingDisks: existingDisks ?? [],
    physicalDrives: physicalDrives ?? [],
  };

  // Load existing disks from VM config
  if (vmId !== null) {
    try {
      const vmConfig = await api.getVm(vmId);
      const disks = (vmConfig as unknown as Record<string, unknown>).disks as DriveConfig[] | undefined;
      currentDrives = disks ?? [];
      currentVmName = (vmConfig as unknown as Record<string, unknown>).name as string || null;
    } catch {
      currentDrives = [];
      currentVmName = null;
    }
  } else {
    currentDrives = [];
    currentVmName = null;
  }

  let form = '<div id="drives-container">';
  form += '<table class="drive-table" style="width:100%">';
  form += '<thead><tr><th>Path/Pool</th><th>Interface</th><th>Cache</th><th></th></tr></thead>';
  form += '<tbody id="drives-tbody"></tbody>';
  form += '</table>';
  form += '</div>';
  form += '<button class="ui-button" type="button" id="btn-add-existing">+ Existing Disk</button> ';
  form += '<button class="ui-button" type="button" id="btn-add-new">+ New Disk</button> ';
  form += '<button class="ui-button" type="button" id="btn-add-physical">+ Physical Drive</button>';

  $('<div></div>')
    .appendTo('body')
    .html('<div><h4>Configure Drives</h4>' + form + '</div>')
    .dialog({
      modal: true,
      title: 'Drive Configuration',
      width: 700,
      autoOpen: true,
      buttons: {
        Save: function () {
          // Collect all drives from the table
          currentDrives = [];
          $('#drives-tbody tr').each(function () {
            const path = $(this).find('.pool-image-path').val() as string;
            const format = $(this).find('.pool-image-format').val() as string || 'qcow2';
            if (path) {
              currentDrives.push({
                path: path,
                interface: $(this).find('.drive-interface').val() as string,
                cache: $(this).find('.drive-cache').val() as string,
                format: format,
              });
            }
          });
          drivesModified = true;
          $(this).dialog('close');
        },
        Cancel: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
      open: function () {
        // Populate existing drives
        for (const drive of currentDrives) {
          addDriveRow(drive);
        }
        // Bind button handlers
        $('#btn-add-existing').on('click', () => addExistingDiskRow());
        $('#btn-add-new').on('click', () => addNewDiskRow());
        $('#btn-add-physical').on('click', () => addPhysicalDriveRow());
      },
    });
}

/**
 * Build interface and cache <option> HTML strings
 */
function buildSelectOptions(opts: DriveOptions, drive: DriveConfig | null): { interfaceOptions: string; cacheOptions: string } {
  let interfaceOptions = '';
  for (const [value, label] of Object.entries(opts.interfaces)) {
    const selected = drive && drive.interface === value ? 'selected' : '';
    interfaceOptions += `<option value="${value}" ${selected}>${escapeHtml(label.split('(')[0].trim())}</option>`;
  }

  let cacheOptions = '';
  for (const [value, label] of Object.entries(opts.cacheModes)) {
    const selected = (drive && drive.cache === value) || (!drive && value === 'none') ? 'selected' : '';
    cacheOptions += `<option value="${value}" ${selected}>${escapeHtml(label.split('(')[0].trim())}</option>`;
  }

  return { interfaceOptions, cacheOptions };
}

/**
 * Add a row for a drive that already has a path (loaded from config or just created)
 */
function addDriveRow(drive: DriveConfig): void {
  if (!driveOptions) return;
  const { interfaceOptions, cacheOptions } = buildSelectOptions(driveOptions, drive);

  const row = `<tr>
    <td>
      <input type="hidden" class="pool-image-path" value="${escapeAttr(drive.path)}">
      <input type="hidden" class="pool-image-format" value="${escapeAttr(drive.format || 'qcow2')}">
      <span>${escapeHtml(drive.path)}</span>
    </td>
    <td><select class="drive-interface">${interfaceOptions}</select></td>
    <td><select class="drive-cache">${cacheOptions}</select></td>
    <td><button class="ui-button remove-drive-btn">X</button></td>
  </tr>`;

  const $row = $(row);
  $row.find('.remove-drive-btn').on('click', function () {
    $(this).closest('tr').remove();
  });
  $('#drives-tbody').append($row);
}

/**
 * Add a row with a dropdown to select from existing disk images
 */
function addExistingDiskRow(): void {
  if (!driveOptions) return;
  const opts = driveOptions;
  const disks = opts.existingDisks;

  // Build disk options grouped by pool
  let diskOptions = '<option value="">Select disk image...</option>';
  for (const disk of disks) {
    const sizeLabel = disk.size_human || `${Math.round(disk.size_bytes / (1024 * 1024 * 1024))} GB`;
    const label = `${disk.name} (${sizeLabel}, ${disk.format})`;

    // Check if disk is a base image or in use by another VM
    const isBase = disk.is_base === true;
    const isInUseByOther = disk.in_use && disk.vm_name && disk.vm_name !== currentVmName;
    const isInUseByCurrent = disk.in_use && disk.vm_name === currentVmName;

    let inUseLabel = '';
    let disabled = '';
    if (isBase) {
      inUseLabel = ' [base image]';
      disabled = ' disabled';
    } else if (isInUseByOther) {
      inUseLabel = ` [in use by ${disk.vm_name}]`;
      disabled = ' disabled';
    } else if (isInUseByCurrent) {
      inUseLabel = ' [current]';
    }

    diskOptions += `<option value="${escapeAttr(disk.path)}" data-format="${escapeAttr(disk.format)}"${disabled}>${escapeHtml(label)}${inUseLabel}</option>`;
  }

  const { interfaceOptions, cacheOptions } = buildSelectOptions(opts, null);

  const row = `<tr>
    <td>
      <select class="existing-disk-select" style="width:100%">${diskOptions}</select>
      <input type="hidden" class="pool-image-path" value="">
      <input type="hidden" class="pool-image-format" value="qcow2">
    </td>
    <td><select class="drive-interface">${interfaceOptions}</select></td>
    <td><select class="drive-cache">${cacheOptions}</select></td>
    <td><button class="ui-button remove-drive-btn">X</button></td>
  </tr>`;

  const $row = $(row);
  $row.find('.remove-drive-btn').on('click', function () {
    $(this).closest('tr').remove();
  });
  $row.find('.existing-disk-select').on('change', function () {
    const path = $(this).val() as string;
    const format = $(this).find('option:selected').data('format') as string || 'qcow2';
    $row.find('.pool-image-path').val(path);
    $row.find('.pool-image-format').val(format);
  });
  $('#drives-tbody').append($row);
}

/**
 * Add a new disk via the shared create-disk dialog, then add as a drive row.
 */
function addNewDiskRow(): void {
  if (!driveOptions) return;
  const opts = driveOptions;
  openCreateDiskDialog(currentHost, (result) => {
    addDriveRow({
      path: result.path,
      interface: Object.keys(opts.interfaces)[0] || 'virtio',
      cache: 'none',
      format: result.format,
    });
  });
}

/**
 * Build <option> HTML for a pool selector dropdown with space info data attributes.
 */
export function buildPoolOptions(pools: Record<string, StoragePoolInfo>): string {
  let html = '<option value="">Select pool...</option>';
  for (const [name, info] of Object.entries(pools)) {
    if (typeof info === 'object') {
      const label = `${info.label} (${info.avail_human} free of ${info.size_human})`;
      html += `<option value="${escapeAttr(name)}" data-used-pct="${info.used_pct}" data-avail="${info.avail_human}" data-size="${info.size_human}">${escapeHtml(label)}</option>`;
    } else {
      html += `<option value="${escapeAttr(name)}">${escapeHtml(String(info))}</option>`;
    }
  }
  return html;
}

/**
 * Update the pool space bar UI next to a pool selector.
 * Expects the bar structure: .pool-selector-container > .pool-space-bar > .pool-space-used + .pool-space-label
 */
export function updatePoolSpaceBar($select: JQuery): void {
  const $container = $select.closest('.pool-selector-container');
  const $bar = $container.find('.pool-space-bar');
  const $used = $bar.find('.pool-space-used');
  const $label = $bar.find('.pool-space-label');

  const $option = $select.find('option:selected');
  const usedPct = $option.data('used-pct');
  const avail = $option.data('avail');

  if (usedPct !== undefined && usedPct !== '') {
    $bar.show();
    $used.css('width', usedPct + '%');
    // Color based on usage
    if (usedPct > 90) {
      $used.css('background', '#e74c3c');
    } else if (usedPct > 75) {
      $used.css('background', '#f39c12');
    } else {
      $used.css('background', '#27ae60');
    }
    $label.text(`${avail} free`);
  } else {
    $bar.hide();
  }
}

export interface CreateDiskResult {
  path: string;
  format: string;
  size_gb: number;
}

/**
 * Open a dialog to create a new disk image in a storage pool.
 * Used by both the VM drives dialog and the storage management page.
 *
 * @param host - Target host (undefined = local)
 * @param onCreated - Callback with the created disk info
 */
export async function openCreateDiskDialog(
  host?: string,
  onCreated?: (result: CreateDiskResult) => void,
): Promise<void> {
  const pools = await apiCall(api.getStoragePools(host), 'Failed to Load Pools');
  if (!pools) return;

  const poolOptions = buildPoolOptions(pools as unknown as Record<string, StoragePoolInfo>);

  const form = `<div class="vm-form">
    <label>Storage Pool</label>
    <div class="form-value">
      <div class="pool-selector-container">
        <select id="create-disk-pool" class="pool-select">${poolOptions}</select>
        <div class="pool-space-bar" style="display:none">
          <div class="pool-space-used"></div>
          <span class="pool-space-label"></span>
        </div>
      </div>
    </div>
    <label>Disk Name</label>
    <div class="form-value"><input id="create-disk-name" placeholder="my-disk"></div>
    <label>Size (GB)</label>
    <div class="form-value"><input type="number" id="create-disk-size" min="1" value="100"></div>
    <p style="font-size: 0.9em; color: #666; grid-column: 1 / -1;">
      Creates a new QCOW2 disk image in the selected storage pool.
    </p>
  </div>`;

  $('<div></div>')
    .appendTo('body')
    .html('<div><h4>Create Disk Image</h4>' + form + '</div>')
    .dialog({
      modal: true,
      title: 'Create Disk Image',
      width: 450,
      open: function () {
        $('#create-disk-pool').on('change', function () {
          updatePoolSpaceBar($(this));
        });
      },
      buttons: {
        Create: async function () {
          const pool = ($('#create-disk-pool').val() as string).trim();
          const name = ($('#create-disk-name').val() as string).trim();
          const sizeGb = parseInt($('#create-disk-size').val() as string) || 0;
          if (!pool) {
            alert('Please select a storage pool');
            return;
          }
          if (!name) {
            alert('Please enter a disk name');
            return;
          }
          if (sizeGb < 1) {
            alert('Please enter a valid size');
            return;
          }
          const parent = $(this);
          declareLoading();
          const result = await apiCall(api.createDisk({ pool, name, size_gb: sizeGb }, host), 'Failed to Create Disk');
          declareLoaded();
          if (result) {
            parent.dialog('close');
            if (onCreated) {
              onCreated(result);
            } else {
              notificationDialog(`Disk created: ${result.path}`);
            }
          }
        },
        Cancel: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
    });
}

/**
 * Add a row for selecting a physical drive
 */
function addPhysicalDriveRow(): void {
  if (!driveOptions) return;
  const opts = driveOptions;
  const drives = opts.physicalDrives || [];

  // Build drive options - filter out mounted and in-use-by-other drives
  let driveOptions2 = '<option value="">Select physical drive...</option>';
  for (const drive of drives) {
    // Skip mounted drives
    if (drive.mounted) continue;

    // Skip drives that are part of a RAID/LVM/etc.
    if (drive.holder) continue;

    // Check if in use by another VM
    const isInUseByOther = drive.in_use && drive.vm_name && drive.vm_name !== currentVmName;
    if (isInUseByOther) continue;

    const label = `${drive.path} - ${drive.model || 'Unknown'} (${drive.size_human})`;
    const inUseLabel = drive.in_use && drive.vm_name === currentVmName ? ' [current]' : '';
    driveOptions2 += `<option value="${escapeAttr(drive.path)}">${escapeHtml(label)}${inUseLabel}</option>`;
  }

  const { interfaceOptions, cacheOptions } = buildSelectOptions(opts, null);

  const row = `<tr>
    <td>
      <select class="physical-drive-select" style="width:100%">${driveOptions2}</select>
      <input type="hidden" class="pool-image-path" value="">
      <input type="hidden" class="pool-image-format" value="raw">
    </td>
    <td><select class="drive-interface">${interfaceOptions}</select></td>
    <td><select class="drive-cache">${cacheOptions}</select></td>
    <td><button class="ui-button remove-drive-btn">X</button></td>
  </tr>`;

  const $row = $(row);
  $row.find('.remove-drive-btn').on('click', function () {
    $(this).closest('tr').remove();
  });
  $row.find('.physical-drive-select').on('change', function () {
    const path = $(this).val() as string;
    $row.find('.pool-image-path').val(path);
    $row.find('.pool-image-format').val('raw');
  });
  $('#drives-tbody').append($row);
}

// Export to window for onclick handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).vmModifyDrives = openDrivesDialog;
}
